// Hideo - image boxes of the dialogs (src/components/fields/ImageBox.vue): what the "Or paste an image URL" field and a
// drop on the box do (logic without UI). An image is stored as "media:<id>" (an uploaded file, src/features/cloud/media.ts), an http(s)
// address (a pasted image URL stays a link; a link given through the box menu is copied like an upload when the site
// allows it, otherwise it stays a link) or, in the profile list, a small data URL.
import { isWebImg } from '../utils/url'

/** What the "Or paste an image URL" field shows for an image: its address only when it comes from a URL */
export const imageUrlText = (value: string): string => (isWebImg(value) ? value.trim() : '')

/** The image after typing in the URL field: a web image address replaces it; emptying the field removes an image taken
 *  from a URL (an uploaded one stays); anything else changes nothing (null) */
export function typedImageUrl(current: string, typed: string): string | null {
  const v = typed.trim()
  if (isWebImg(v)) return v === current ? null : v
  if (!v && isWebImg(current)) return ''
  return null
}

/** What a drop on an image box does: read an image file, take a web image's address (only where the box takes URLs), or
 *  tell the user why nothing happens: 'notImage' (a file that is not an image), 'saveFirst' (a web image the box cannot
 *  use: a link that is not an image address), 'fileOnly' (the box takes only files) */
type ImageDrop =
  | { kind: 'file'; index: number }
  | { kind: 'url'; url: string }
  | { kind: 'alert'; alert: 'notImage' | 'saveFirst' | 'fileOnly' }
  | { kind: 'none' }

/** files = the dropped files' types; text = text/uri-list or text/plain of the drop (an image dragged from a web page);
 *  acceptsUrl = the box keeps web image addresses */
export function imageDrop(files: readonly { type: string }[], text: string, acceptsUrl: boolean): ImageDrop {
  const index = files.findIndex(f => f.type.startsWith('image/'))
  if (index >= 0) return { kind: 'file', index }
  if (files.length) return { kind: 'alert', alert: 'notImage' }
  const url = (text || '').split('\n')[0].trim()
  if (acceptsUrl && isWebImg(url)) return { kind: 'url', url }
  if (url) return { kind: 'alert', alert: acceptsUrl ? 'saveFirst' : 'fileOnly' }
  return { kind: 'none' }
}

// ===== An image taken from its link (the "Image link" choice of the box menu, the icon dialog) =====
/** How long the copy of a linked image may take before the link is kept as it is */
const LINK_FETCH_MS = 8000

/** The image file behind a web address, or null when the site doesn't let the app read it (most sites don't allow it:
 *  CORS), it isn't an image, or it takes too long */
export async function fetchWebImage(url: string, ms = LINK_FETCH_MS): Promise<Blob | null> {
  if (!isWebImg(url)) return null
  const ctl = typeof AbortController === 'function' ? new AbortController() : null
  const timer = ctl ? setTimeout(() => ctl.abort(), ms) : 0
  try {
    const r = await fetch(url.trim(), { mode: 'cors', credentials: 'omit', referrerPolicy: 'no-referrer', signal: ctl?.signal })
    if (!r.ok) return null
    const b = await r.blob()
    return b.type.startsWith('image/') && b.size > 0 ? b : null
  } catch { return null } finally { if (timer) clearTimeout(timer) }
}

/** What an image box keeps for a pasted image link: a copy stored like an uploaded file when the site allows reading it
 *  (`store` shrinks and stores the file, as for an upload), otherwise the address itself (shown straight from the web).
 *  '' when the text is not a web image address */
export async function imageFromLink(text: string, store: (b: Blob) => Promise<string>, fetchImg = fetchWebImage): Promise<string> {
  const url = String(text || '').trim()
  if (!isWebImg(url)) return ''
  const b = await fetchImg(url)
  if (b) {
    // readImg never settles for a file the browser can't draw: after a while the link is kept
    let timer: ReturnType<typeof setTimeout> | undefined
    const late = new Promise<never>((_, rej) => { timer = setTimeout(() => rej(new Error('timeout')), LINK_FETCH_MS) })
    try { return await Promise.race([store(b), late]) } catch { /* unreadable image: keep the link */ } finally { clearTimeout(timer) }
  }
  return url
}
